/**
 * CritiqueModeFooterControls - Footer right-well controls for critique mode
 * Feature: 072-footer-redesign
 *
 * Provides layout selector, zoom control, and view mode buttons
 * Published to FooterContext for display in unified footer
 */

import { Button } from '@/components/atoms/Button';
import { LayoutModeSelector } from '@/components/layout/Footer/LayoutModeSelector';
import { ZoomLevelSelector } from '@/components/layout/Footer/ZoomLevelSelector';
import { FullscreenIcon } from '@/components/icons/FullscreenIcon';
import type { ZoomLevel } from '@/hooks/useZoomPan';
import type { ViewMode } from '@/contexts/ViewModeContext';

export type LayoutMode = 'list' | 'lightroomClone' | 'grid' | 'magazine';

export interface LayoutOption {
  value: string;
  label: string;
  icon: JSX.Element;
}

interface CritiqueModeFooterControlsProps {
  layoutMode: LayoutMode;
  layoutOptions: LayoutOption[];
  sessionZoomLevel: ZoomLevel;
  viewMode: ViewMode;
  onLayoutChange: (mode: LayoutMode) => void;
  onZoomChange: (level: ZoomLevel) => void;
  onToggleDimmedLights: () => void;
  onToggleFullscreen: () => void;
}

export function CritiqueModeFooterControls({
  layoutMode,
  layoutOptions,
  sessionZoomLevel,
  viewMode,
  onLayoutChange,
  onZoomChange,
  onToggleDimmedLights,
  onToggleFullscreen,
}: CritiqueModeFooterControlsProps) {
  return (
    <div className="flex items-center gap-4">
      {/* Layout mode selector */}
      <LayoutModeSelector
        value={layoutMode}
        options={layoutOptions}
        onChange={(value) => onLayoutChange(value as LayoutMode)}
        aria-label="View layout selector"
      />

      {/* Zoom level selector (Feature 646-critique-zoom-control) */}
      <ZoomLevelSelector
        currentZoom={sessionZoomLevel}
        onChange={onZoomChange}
      />

      {/* View mode controls - dimming and fullscreen */}
      <div className="flex gap-1 items-center">
        <Button
          onClick={onToggleDimmedLights}
          variant="transparent"
          size="compact"
          className={viewMode === 'dimmedLights' ? 'bg-surface-2' : ''}
          title="Dim surrounding UI to focus on photos (L)"
          aria-label="Toggle dim lights mode"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </Button>
        <Button
          onClick={onToggleFullscreen}
          variant="transparent"
          size="compact"
          className={viewMode === 'fullscreen' ? 'bg-surface-2' : ''}
          title="Toggle fullscreen mode (F)"
          aria-label="Toggle fullscreen mode"
        >
          <FullscreenIcon size={16} className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
